import React, { useEffect, useState } from 'react';
import { Grid, makeStyles, Box } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import ErrorRedirectButton from '../../components/core/ErrorRedirectButton';
import ErrorSnippet from '../../components/core/ErrorSnippet';
import PageSection from '../../components/core/PageSection';
import { actionResetErrorState } from '../../store/Error/ErrorAction';
import { useComponentWillUnmount } from '../../utils/useComponentWillUnmount';
import { ERROR_PAGE_TYPES } from '../../utils/constants/ErrorPageTypes';
import { useAuth } from '../../utils/useAuth';
import ErrorCloseButton from '../../components/core/ErrorCloseButton';
import ErrorSignoutButton from '../../components/core/ErrorSignoutButton';
import { trackErrorPageView } from '../../utils/useOneTag';
import { noticeError } from '../../utils/newRelic';
import clsx from 'clsx';

const useStyles = makeStyles((theme) => ({
  root: {
    alignItems: 'center',
    bottom: 0,
    justifyContent: 'center',
    left: 0,
    overflow: 'hidden',
    right: 0,
    top: 0,
    minHeight: '60vh',
    display: 'flex',
    flexGrow: 1,
  },
  container: {
    paddingLeft: theme.spacing(2.5),
    paddingRight: theme.spacing(2.5),
    [theme.breakpoints.down(650)]: {
      flexGrow: 'unset',
      maxWidth: 'unset',
      flexBasis: 'unset',
    },
  },
  button: {
    marginBottom: theme.spacing(2),
    minHeight: 48,
    height: 'auto',
    maxWidth: 328,
    display: 'flex',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
    '& button': {
      borderRadius: theme.spacing(1),
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
  },
  buttonDark: {
    '& button': {
      backgroundColor: theme.palette.beacon.atlantic,
    },
  },
  buttonLight: {
    '& button': {
      color: '#306E88',
    },
  },
}));

const DefaultError = ({ type: errorTypeProp, showButton: showButtonProp }) => {
  const classes = useStyles();
  const { errorType } = useSelector((state) => state?.error);
  const { user } = useAuth();
  const location = useLocation();

  const [showButton, setShowButton] = useState(showButtonProp ?? false);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    let _error = ERROR_PAGE_TYPES[errorType ?? errorTypeProp];
    if (_error == null) {
      _error = ERROR_PAGE_TYPES.PAGE_NOT_FOUND;
      if (
        user.session.isAuthenticated &&
        Date.now() < user.session.sessionExpiry
      ) {
        noticeError(_error.value, { path: location.pathname });
      }
    } else if (_error.value === ERROR_PAGE_TYPES.UNAUTHORIZED.value) {
      trackErrorPageView(_error);
      history.push('/sign-out');
      return;
    } else if (
      _error.value === ERROR_PAGE_TYPES.MAINTENANCE.value &&
      (!user.session.isAuthenticated || Date.now() > user.session.sessionExpiry)
    ) {
      _error = ERROR_PAGE_TYPES.MAINTENANCE_NO_SIGNOUT;
    }
    // Use GENERIC_NO_BUTTON error page type instead of GENERIC if on home page
    if (
      location.pathname === '/' &&
      _error.value === ERROR_PAGE_TYPES.GENERIC.value
    ) {
      _error = ERROR_PAGE_TYPES.GENERIC_NO_BUTTON;
    }
    trackErrorPageView(_error);
    setError(_error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    errorType,
    errorTypeProp,
    user.session.isAuthenticated,
    location.pathname,
    history,
  ]);

  useEffect(() => {
    if (error?.buttonText != null && error?.buttonUrl != null) {
      setShowButton(true);
    }
  }, [error, user.session.isAuthenticated]);

  useComponentWillUnmount(() => {
    dispatch(actionResetErrorState());
  });

  return (
    error != null && (
      <PageSection
        className={classes.root}
        variant="grid"
        id="error-page-section">
        {error.onCloseUrl != null && (
          <ErrorCloseButton onCloseUrl={error.onCloseUrl} />
        )}
        <Grid className={classes.container} item xs={12} sm={10}>
          <Box role="status">
            <ErrorSnippet type={error.value} />
          </Box>
          {showButton && (
            <Box className={clsx(classes.buttonContainer, classes.buttonDark)}>
              <ErrorRedirectButton type={error.value} />
            </Box>
          )}
          {error.shouldShowSignoutButton && (
            <Box className={clsx(classes.buttonContainer, classes.buttonLight)}>
              <ErrorSignoutButton />
            </Box>
          )}
        </Grid>
      </PageSection>
    )
  );
};

export default DefaultError;
